import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { listCustomDomains, refreshAllCustomDomains, refreshCustomDomain, resolveDomainState } from '@/lib/customDomain';
import { APP_DOMAIN } from '@/lib/domain';

interface DomainRow {
  id: number;
  domaine: string | null;
  user_id: number | null;
  hostname_status: string | null;
  ssl_status: string | null;
  is_active: boolean;
  created_at: string | null;
  user_name: string | null;
  user_name_label: string | null;
  user_email: string | null;
}

const TONE_CLASSES: Record<string, string> = {
  neutral: 'bg-background-100 text-foreground-500',
  warn: 'bg-amber-100 text-amber-700',
  info: 'bg-primary-100 text-primary-700',
  success: 'bg-green-100 text-green-700',
  danger: 'bg-red-100 text-red-600',
};

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function SuperAdminDomainsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState<DomainRow[]>([]);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkingId, setCheckingId] = useState<number | null>(null);
  const [bulkChecking, setBulkChecking] = useState(false);
  const [bulkResult, setBulkResult] = useState<{ tone: 'success' | 'danger'; message: string } | null>(null);
  const [rowFeedback, setRowFeedback] = useState<{ id: number; tone: 'success' | 'danger'; message: string } | null>(null);

  const fetchDomains = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listCustomDomains({ userId: user.id, userEmail: user.email });
      if (res.error) {
        setError(res.error);
      } else {
        setRows((res.domains as unknown as DomainRow[]) || []);
        setConfigured(!!res.configured);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  const handleCheck = useCallback(
    async (row: DomainRow) => {
      if (!user) return;
      setCheckingId(row.id);
      setRowFeedback(null);
      try {
        const res = await refreshCustomDomain({ userId: user.id, userEmail: user.email, domainId: row.id });
        if (res.error) {
          setRowFeedback({ id: row.id, tone: 'danger', message: res.error });
          return;
        }
        setRows((prev) =>
          prev.map((r) =>
            r.id === row.id
              ? {
                  ...r,
                  hostname_status: res.hostname_status ?? r.hostname_status,
                  ssl_status: res.ssl_status ?? r.ssl_status,
                  is_active: res.is_active ?? r.is_active,
                }
              : r,
          ),
        );
        setRowFeedback({
          id: row.id,
          tone: 'success',
          message: res.is_active ? 'Domaine actif et sécurisé (SSL validé).' : 'Statut mis à jour.',
        });
      } catch (err) {
        setRowFeedback({
          id: row.id,
          tone: 'danger',
          message: err instanceof Error ? err.message : 'Erreur de vérification',
        });
      } finally {
        setCheckingId(null);
      }
    },
    [user],
  );

  const handleCheckAll = useCallback(async () => {
    if (!user) return;
    setBulkChecking(true);
    setBulkResult(null);
    try {
      const res = await refreshAllCustomDomains({ userId: user.id, userEmail: user.email });
      if (res.error) {
        setBulkResult({ tone: 'danger', message: res.error });
      } else {
        const total = res.total || 0;
        const expired = res.expired || 0;
        setBulkResult({
          tone: expired > 0 ? 'danger' : 'success',
          message:
            expired > 0
              ? `${expired} domaine(s) sur ${total} nécessitent une attention (SSL non validé ou domaine désactivé).`
              : `${total} domaine(s) vérifié(s) — tous opérationnels.`,
        });
      }
      await fetchDomains();
    } catch (err) {
      setBulkResult({ tone: 'danger', message: err instanceof Error ? err.message : 'Erreur de vérification' });
    } finally {
      setBulkChecking(false);
    }
  }, [user, fetchDomains]);

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
            Domaines personnalisés
          </h2>
          <p className="text-sm text-foreground-500 mt-1">
            Domaines connectés par les commerçants via Cloudflare for SaaS.
          </p>
        </div>
        <button
          onClick={fetchDomains}
          disabled={loading}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap bg-background-50 border border-background-200/70 text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer disabled:opacity-50"
        >
          {loading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-refresh-line"></i>}
          Actualiser
        </button>
        <button
          onClick={handleCheckAll}
          disabled={bulkChecking || loading}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50"
          title="Re-vérifie tous les domaines auprès de Cloudflare"
        >
          {bulkChecking ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-radar-line"></i>}
          Tout vérifier
        </button>
      </div>

      {bulkResult && (
        <div
          className={`mb-4 px-4 py-3 rounded-lg text-sm flex items-center gap-2 ${
            bulkResult.tone === 'success'
              ? 'bg-background-100 text-foreground-700'
              : 'bg-red-50 border border-red-200 text-red-600'
          }`}
        >
          <i className={bulkResult.tone === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
          {bulkResult.message}
        </div>
      )}

      {configured === false && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          Cloudflare n&apos;est pas configuré. Renseignez CLOUDFLARE_API_TOKEN et CLOUDFLARE_ZONE_ID
          dans les secrets du backend.
        </div>
      )}

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center">
            <i className="ri-global-line text-3xl text-foreground-300"></i>
            <p className="text-sm text-foreground-500 mt-3">Aucun domaine personnalisé connecté.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="border-b border-background-200/70 text-left text-xs text-foreground-500">
                  <th className="px-4 py-3 font-medium">Commerçant</th>
                  <th className="px-4 py-3 font-medium">Boutique</th>
                  <th className="px-4 py-3 font-medium">Sous-domaine Zifek</th>
                  <th className="px-4 py-3 font-medium">Domaine personnalisé</th>
                  <th className="px-4 py-3 font-medium">Hostname</th>
                  <th className="px-4 py-3 font-medium">SSL</th>
                  <th className="px-4 py-3 font-medium">Statut</th>
                  <th className="px-4 py-3 font-medium">Ajouté le</th>
                  <th className="px-4 py-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const status = resolveDomainState(r.hostname_status, r.ssl_status, r.is_active);
                  return (
                    <tr key={r.id} className="border-b border-background-200/40 last:border-0 hover:bg-background-100/50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground-900">{r.user_name_label || '—'}</div>
                        <div className="text-xs text-foreground-400">{r.user_email || '—'}</div>
                      </td>
                      <td className="px-4 py-3 text-foreground-600 font-mono text-xs">{r.user_name || '—'}</td>
                      <td className="px-4 py-3">
                        {r.user_name ? (
                          <a
                            href={`https://${r.user_name}.${APP_DOMAIN}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-700 text-xs font-mono break-all"
                          >
                            {r.user_name}.{APP_DOMAIN}
                          </a>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground-800 break-all">{r.domaine || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground-600">{r.hostname_status || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground-600">{r.ssl_status || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 ${TONE_CLASSES[status.tone]}`}>
                          <i className={status.icon}></i>{status.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-foreground-500 whitespace-nowrap">{formatDate(r.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleCheck(r)}
                            disabled={checkingId === r.id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap bg-primary-100 text-primary-700 hover:bg-primary-200/70 transition-colors cursor-pointer disabled:opacity-50"
                          >
                            {checkingId === r.id ? (
                              <i className="ri-loader-4-line animate-spin"></i>
                            ) : (
                              <i className="ri-refresh-line"></i>
                            )}
                            Vérifier
                          </button>
                          {r.domaine && (
                            <a
                              href={`https://${r.domaine}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors"
                            >
                              <i className="ri-external-link-line"></i>Ouvrir
                            </a>
                          )}
                        </div>
                        {rowFeedback?.id === r.id && (
                          <div
                            className={`mt-2 text-xs whitespace-nowrap ${
                              rowFeedback.tone === 'success' ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {rowFeedback.message}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}